import React, { useEffect, useRef } from "react";
import styled from "styled-components";

interface InlineLoadingProps {
  /** ローディングの表示/非表示 */
  visible?: boolean;
  /** サイズ（デフォルト: 32px） */
  size?: number;
}

const LoadingContainer = styled.div<{ $visible: boolean; $size: number }>`
  display: ${props => props.$visible ? 'flex' : 'none'};
  justify-content: center;
  align-items: center;
  width: 100%;
  min-height: ${props => props.$size + 16}px;
  padding: 8px 0;
`;

const LoadingWrapper = styled.div<{ $size: number }>`
  width: ${props => props.$size}px;
  height: ${props => props.$size}px;
  display: inline-block;
`;

/**
 * インラインローディングコンポーネント
 * 無限スクロールなど、コンテンツ内でのローディング表示に使用
 */
const InlineLoading: React.FC<InlineLoadingProps> = ({ 
  visible = true, 
  size = 32
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!visible) return;

    let animationId: number;

    function animationLoading(time: number) {
      const container = containerRef.current;
      if (!container) return;
      
      const circles = container.querySelectorAll<SVGGElement>(".inline-circle");
      circles.forEach((circle, index) => {
        const angle = (time * (2 - index / 10)) / 5;
        circle.setAttribute("transform", `rotate(${angle} 0 0)`);
      });

      animationId = requestAnimationFrame(animationLoading);
    }

    animationId = requestAnimationFrame(animationLoading);

    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [visible]);

  return (
    <LoadingContainer ref={containerRef} $visible={visible} $size={size}>
      <LoadingWrapper $size={size}>
        <svg viewBox="0 0 100 100">
          <defs>
            <filter id="inline-goo">
              <feGaussianBlur in="SourceGraphic" stdDeviation="8" result="blur" />
              <feColorMatrix
                in="blur"
                mode="matrix"
                values="
                1 0 0 0 0
                0 1 0 0 0
                0 0 1 0 0
                0 0 0 25 -8"
                result="goo"
              />
              <feBlend in="SourceGraphic" in2="goo" />
            </filter>
          </defs>
          <g filter="url(#inline-goo)" fill="#f0f0f0" stroke="#fcfcfc">
            <g transform="translate(50, 50)">
              <g className="inline-circle -a">
                <g transform="translate(-50, -50)">
                  <circle cx="25" cy="50" r="9" />
                </g>
              </g>
            </g>
            <g transform="translate(50, 50)">
              <g className="inline-circle -b">
                <g transform="translate(-50, -50)">
                  <circle cx="50" cy="25" r="8" />
                </g>
              </g>
            </g>
            <g transform="translate(50, 50)">
              <g className="inline-circle -c">
                <g transform="translate(-50, -50)">
                  <circle cx="75" cy="50" r="7" />
                </g>
              </g>
            </g>
            <g transform="translate(50, 50)">
              <g className="inline-circle -d">
                <g transform="translate(-50, -50)">
                  <circle cx="50" cy="75" r="6" />
                </g>
              </g>
            </g>
            <g transform="translate(50, 50)">
              <g className="inline-circle -e">
                <g transform="translate(-50, -50)">
                  <circle cx="25" cy="50" r="5" />
                </g>
              </g>
            </g>
            <g transform="translate(50, 50)">
              <g className="inline-circle -f">
                <g transform="translate(-50, -50)">
                  <circle cx="50" cy="25" r="4" />
                </g>
              </g>
            </g>
            <g transform="translate(50, 50)">
              <g className="inline-circle -g">
                <g transform="translate(-50, -50)">
                  <circle cx="75" cy="50" r="3" />
                </g>
              </g>
            </g>
            <g transform="translate(50, 50)">
              <g className="inline-circle -h">
                <g transform="translate(-50, -50)">
                  <circle cx="50" cy="75" r="2" />
                </g>
              </g>
            </g>
          </g>
        </svg>
      </LoadingWrapper>
    </LoadingContainer>
  );
};

export default InlineLoading;

